import { decorators, ComponentChanged, OBSERVER_TYPE, GameObject } from '@combos-fun/engine';
import { Renderer3D, Renderer3DSystem } from '@combos-fun/plugin-renderer-3d';
import {
  TextureLoader,
  PlaneGeometry,
  MeshBasicMaterial,
  Mesh,
  DoubleSide,
} from 'three';
import type { Texture } from 'three';
import SpriteAnimation3D from './component';

interface FrameData {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface SpriteAnimation3DEntry {
  mesh: Mesh;
  texture: Texture;
  frames: FrameData[];
  sheetWidth: number;
  sheetHeight: number;
  currentFrame: number;
  elapsed: number;
  playing: boolean;
}

@decorators.componentObserver({
  SpriteAnimation3D: [
    'resource', 'speed',
    'positionX', 'positionY', 'positionZ',
    'rotationX', 'rotationY', 'rotationZ',
    'scaleX', 'scaleY',
  ],
})
export default class SpriteAnimation3DSystem extends Renderer3D {
  static systemName = 'SpriteAnimation3DSystem';
  name: string = 'SpriteAnimation3DSystem';

  private entries: Map<number, SpriteAnimation3DEntry> = new Map();
  private textureLoader = new TextureLoader();

  init() {
    const renderer3DSystem = this.game.getSystem(Renderer3DSystem) as Renderer3DSystem;
    renderer3DSystem.rendererManager.register(this);
  }

  componentChanged(changed: ComponentChanged) {
    if (changed.componentName !== 'SpriteAnimation3D') return;

    if (changed.type === OBSERVER_TYPE.ADD) {
      this.handleAdd(changed.gameObject, changed.component as SpriteAnimation3D);
    } else if (changed.type === OBSERVER_TYPE.REMOVE) {
      this.handleRemove(changed.gameObject.id);
    } else {
      this.handleChange(changed);
    }
  }

  rendererUpdate(gameObject: GameObject) {
    const component = gameObject.getComponent(SpriteAnimation3D) as SpriteAnimation3D;
    if (!component) return;

    const entry = this.entries.get(gameObject.id);
    if (!entry) return;

    entry.mesh.position.set(component.positionX, component.positionY, component.positionZ);
    entry.mesh.rotation.set(component.rotationX, component.rotationY, component.rotationZ);
    entry.mesh.scale.set(component.scaleX, component.scaleY, 1);

    if (!entry.playing || entry.frames.length === 0) return;

    const delta = this.threeContext.clock.getDelta() * 1000;
    entry.elapsed += delta;

    if (entry.elapsed >= component.speed) {
      entry.elapsed -= component.speed;
      entry.currentFrame = (entry.currentFrame + 1) % entry.frames.length;
      this.applyFrame(entry);
    }
  }

  private async handleAdd(gameObject: GameObject, component: SpriteAnimation3D) {
    if (!component.resource) return;

    const asyncId = this.increaseAsyncId(gameObject.id);

    try {
      const response = await fetch(component.resource);
      const json = await response.json();
      if (!this.validateAsyncId(gameObject.id, asyncId)) return;

      const meta = json.meta;
      const framesData = json.frames;
      const imageUrl = this.resolveImageUrl(component.resource, meta.image);

      const texture = await this.loadTexture(imageUrl);
      if (!this.validateAsyncId(gameObject.id, asyncId)) {
        texture.dispose();
        return;
      }

      const frames = this.parseFrames(framesData);
      const sheetWidth = meta.size.w;
      const sheetHeight = meta.size.h;

      const firstFrame = frames[0];
      const aspectRatio = firstFrame.w / firstFrame.h;
      const geometry = new PlaneGeometry(aspectRatio, 1);
      const material = new MeshBasicMaterial({
        map: texture,
        transparent: true,
        side: DoubleSide,
      });
      const mesh = new Mesh(geometry, material);

      const entry: SpriteAnimation3DEntry = {
        mesh,
        texture,
        frames,
        sheetWidth,
        sheetHeight,
        currentFrame: 0,
        elapsed: 0,
        playing: component.autoPlay,
      };

      this.applyFrame(entry);

      mesh.position.set(component.positionX, component.positionY, component.positionZ);
      mesh.rotation.set(component.rotationX, component.rotationY, component.rotationZ);
      mesh.scale.set(component.scaleX, component.scaleY, 1);

      this.threeContext.scene.add(mesh);
      this.entries.set(gameObject.id, entry);
    } catch (e) {
      console.error('SpriteAnimation3DSystem: failed to load spritesheet', e);
    }
  }

  private handleChange(changed: ComponentChanged) {
    const component = changed.component as SpriteAnimation3D;
    const changedProp = changed.prop?.prop?.[0];

    if (changedProp === 'resource') {
      this.handleRemove(changed.gameObject.id);
      this.handleAdd(changed.gameObject, component);
    }
  }

  private handleRemove(id: number) {
    this.increaseAsyncId(id);
    const entry = this.entries.get(id);
    if (!entry) return;

    this.threeContext.scene.remove(entry.mesh);
    entry.mesh.geometry.dispose();
    (entry.mesh.material as MeshBasicMaterial).dispose();
    entry.texture.dispose();
    this.entries.delete(id);
  }

  private applyFrame(entry: SpriteAnimation3DEntry) {
    const frame = entry.frames[entry.currentFrame];
    const tex = entry.texture;

    tex.offset.set(frame.x / entry.sheetWidth, 1 - (frame.y + frame.h) / entry.sheetHeight);
    tex.repeat.set(frame.w / entry.sheetWidth, frame.h / entry.sheetHeight);
  }

  private parseFrames(framesData: any): FrameData[] {
    if (Array.isArray(framesData)) {
      return framesData.map((f: any) => ({
        x: f.frame.x,
        y: f.frame.y,
        w: f.frame.w,
        h: f.frame.h,
      }));
    }
    // Object-based format: { "frameName": { frame: { x, y, w, h } } }
    return Object.keys(framesData)
      .sort()
      .map((key) => ({
        x: framesData[key].frame.x,
        y: framesData[key].frame.y,
        w: framesData[key].frame.w,
        h: framesData[key].frame.h,
      }));
  }

  private resolveImageUrl(jsonUrl: string, imageName: string): string {
    const base = jsonUrl.substring(0, jsonUrl.lastIndexOf('/') + 1);
    return base + imageName;
  }

  private loadTexture(url: string): Promise<Texture> {
    return new Promise((resolve, reject) => {
      this.textureLoader.load(url, resolve, undefined, reject);
    });
  }

  onDestroy() {
    for (const [id] of this.entries) {
      this.handleRemove(id);
    }
    this.entries.clear();
  }
}
